import { useComponents } from 'src/store/modules/App/component'
import type { Component } from '@/types'
import type {
  ComponentMetadata,
  FigmaComponentMetadata,
} from '@/types/components'
import arrayUtils from '@utils/arrayUtils.js'
import { humanize } from '@utils/formats/index'
import { isArray, hasObjectConstructor } from '@/utils/checkers/validation'

/**
 * @typedef ComponentSchema
 * @property {String} name
 * @property {String} display_name
 */

/**
 * @param  {String} componentName
 * @param  {String} fieldKey
 * @return {String}
 */
export const getSanitizedComponentFieldName = (
  fieldKey: string,
  componentName?: Component['name'],
): string => {
  if (!componentName) {
    return humanize(fieldKey)
  }

  const componentStore = useComponents()

  const componentSchema = componentStore.components.find(
    (component) => component.name === componentName,
  )

  const schema = componentSchema?.schema

  if (schema && componentSchema && fieldKey in schema) {
    return humanizeComponentName(componentSchema.schema[fieldKey], fieldKey)
  }

  return humanize(fieldKey)
}

/**
 * @param  {Array<ComponentSchema>} components
 * @param  {String} name
 * @return {String}
 */
export const getSanitizedComponentName = (name: unknown): string => {
  const componentStore = useComponents()
  const componentSchema = componentStore.components.find(
    (component) => component.name === name,
  )

  return humanizeComponentName(componentSchema, name)
}

/**
 * @param  {ComponentSchema} componentSchema
 * @param  {String} name
 * @return {String}
 */
export const humanizeComponentName = (componentSchema, name): unknown => {
  if (componentSchema && componentSchema.display_name) {
    return componentSchema.display_name
  }

  return humanize(name)
}

export const renameComponent = (storyContent, oldName, newName): unknown => {
  if (oldName === newName) {
    return storyContent
  }

  // this clear the story content cache used into the breadcrumbs of the block library
  arrayUtils.clearCache()

  const newStoryContent = arrayUtils.clone(storyContent)

  function justRename(json, oldComponentName, newComponentName): unknown {
    if (hasObjectConstructor(json)) {
      if (json.component === oldComponentName) {
        json.component = newComponentName
      }

      Object.keys(json).forEach((key) => {
        if (hasObjectConstructor(json[key]) || isArray(json[key])) {
          justRename(json[key], oldComponentName, newComponentName)
        }
      })
    }

    if (isArray(json)) {
      json.forEach((item) => {
        if (hasObjectConstructor(item) || isArray(item)) {
          justRename(item, oldComponentName, newComponentName)
        }
      })
    }
  }

  justRename(newStoryContent, oldName, newName)

  return newStoryContent
}

export const findComponentMetadata = (
  component: Component,
  appSlug: string,
): ComponentMetadata | undefined => {
  if (!('metadata' in component) || !Array.isArray(component.metadata)) {
    return
  }

  return component.metadata.find((metadata) => metadata.app_slug === appSlug)
}

export const findFigmaComponentMetadata = (
  component: Component,
): FigmaComponentMetadata | undefined =>
  findComponentMetadata(component, 'figma_to_storyblok')
